'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { faFilter, faXmark } from '@icons/classic/regular';
import { Button, DropdownMenuGenerator, FlexContainer } from '../components';
import { getFilterKey, shouldShowFilter } from './filter-active';
import { useTableFilters } from './useTableFilters';
import TableFilter from './TableFilter';
import type { MenuItem } from '../components';
import type { TableFilterProps, TableFilterType } from './TableFilter';
import type { FilterColumnsConfig, FilterConfigMap } from './typed-filter-config';
export interface CollapsibleTableFiltersProps<T extends FilterConfigMap = FilterConfigMap> {
  config: FilterColumnsConfig;
  filterType: TableFilterType;
  showResetButton: boolean;
  className?: string;
  /** Filter keys (their `searchParam`) to always show, skipping "Add filter" and the remove (✕) control. */
  alwaysVisible?: (keyof T & string)[];
}

/** Menu label for a filter: explicit `label`, else its placeholder, else its key. */
function getFilterLabel(props: TableFilterProps): string {
  if (props.label) return props.label;
  if ('placeholder' in props && typeof props.placeholder === 'string' && props.placeholder) {
    return props.placeholder;
  }
  return getFilterKey(props);
}

/**
 * A decluttered filter bar: it shows only the filters that currently hold a value
 * (or were added this session) and offers an "Add filter" menu for the rest. A
 * filter shows a remove (✕) control only once it's back at its default — an active
 * filter is cleared through its own control first.
 *
 * URL state is unchanged from the standard bar; the only extra state is the local
 * set of filters drafted via the menu, which is not persisted (a filter added but
 * left at its default disappears on refresh; one with a value reappears because
 * it's in the URL). Filters made active by navigation are never treated as drafts,
 * so they drop off the bar as soon as they return to their default.
 *
 * `alwaysVisible` pins filters to the bar unconditionally — they never enter "Add
 * filter" and never get a remove control, so they must be changed via the filter's
 * own control.
 */
export function CollapsibleTableFilters(t0) {
  const $ = _c(21);
  const {
    config,
    filterType,
    showResetButton,
    className,
    alwaysVisible
  } = t0;
  const searchParams = useSearchParams();
  const {
    activeFilterParams,
    clearAllFilters
  } = useTableFilters(filterType);
  const filters = [...config.left, ...(config.right ?? [])];
  const params = searchParams ?? new URLSearchParams();
  const alwaysVisibleKeys = new Set(alwaysVisible);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [draftKeys, setDraftKeys] = useState(t1);
  const visibleFilters = filters.filter(filter => shouldShowFilter(filter, params) || draftKeys.has(getFilterKey(filter)) || alwaysVisibleKeys.has(getFilterKey(filter)));
  const addMenuItems = filters.filter(filter_0 => !shouldShowFilter(filter_0, params) && !draftKeys.has(getFilterKey(filter_0)) && !alwaysVisibleKeys.has(getFilterKey(filter_0))).map(filter_1 => {
    const key = getFilterKey(filter_1);
    return {
      type: "item",
      id: key,
      label: getFilterLabel(filter_1),
      onClick: () => setDraftKeys(prev => new Set(prev).add(key))
    };
  });
  const removeFilter = function removeFilter(key_0) {
    setDraftKeys(prev_0 => {
      const next = new Set(prev_0);
      next.delete(key_0);
      return next;
    });
  };
  let t2;
  if ($[1] !== clearAllFilters) {
    t2 = function handleReset() {
      clearAllFilters();
      setDraftKeys(new Set());
    };
    $[1] = clearAllFilters;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleReset = t2;
  const T0 = FlexContainer;
  let t3;
  if ($[3] !== className) {
    t3 = twMerge("flex-col gap-2 md:flex-row md:items-start md:justify-between", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const T1 = FlexContainer;
  const t4 = "flex-1 flex-wrap gap-2 md:gap-3";
  const t5 = "center";
  const t6 = visibleFilters.map(props => {
    const key_1 = getFilterKey(props);
    return <FlexContainer className="gap-1" items="center" key={key_1}><TableFilter {...props} filterType={filterType} grow={false} />{!shouldShowFilter(props, params) && !alwaysVisibleKeys.has(key_1) && <Button className="size-10" aria-label={`Remove ${getFilterLabel(props)} filter`} onClick={() => removeFilter(key_1)} size="icon" variant="tertiary"><FontAwesomeIcon icon={faXmark} /></Button>}</FlexContainer>;
  });
  let t7;
  if ($[5] !== addMenuItems) {
    t7 = addMenuItems.length > 0 && <DropdownMenuGenerator config={{
      align: "start",
      items: addMenuItems,
      trigger: <Button size="lg" variant="tertiary"><FontAwesomeIcon icon={faFilter} />Add filter</Button>
    }} />;
    $[5] = addMenuItems;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== T1 || $[8] !== t6 || $[9] !== t7) {
    t8 = <T1 className={t4} items={t5}>{t6}{t7}</T1>;
    $[7] = T1;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== activeFilterParams || $[12] !== draftKeys || $[13] !== handleReset || $[14] !== showResetButton) {
    t9 = showResetButton && (activeFilterParams.length > 0 || draftKeys.size > 0) && <Button className="h-10" onClick={handleReset} size="md" variant="quaternary">Reset</Button>;
    $[11] = activeFilterParams;
    $[12] = draftKeys;
    $[13] = handleReset;
    $[14] = showResetButton;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== T0 || $[17] !== t3 || $[18] !== t8 || $[19] !== t9) {
    t10 = <T0 className={t3}>{t8}{t9}</T0>;
    $[16] = T0;
    $[17] = t3;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
export default CollapsibleTableFilters;